import {css, html, LitElement} from "lit";
import {PropertyValues} from "@lit/reactive-element";
import {pxToRem} from "../styles";
import {WlSelected} from "../events/WlSelected";

export class WlRange extends LitElement {
    static formAssociated = true;

    // @ts-ignore
    private _internals: ElementInternals & IElementInternals;

    constructor() {
        super();
        this._internals = this.attachInternals();
    }

    clear() {
        this.getChildren().forEach(child => child.clear());
    }

    static get styles() {
        return css`
            :host {
                display: flex;
                gap: ${pxToRem(4)};
                padding: ${pxToRem(10)};
            }
        `
    }

    render() {
        return html`
            <slot @change="${this.updatedItems}"></slot>
        `
    }

    protected firstUpdated(_changedProperties: PropertyValues) {
        this.updatedItems();
    }

    private updatedItems() {
        let children = this.getChildren();
        if (children.length == 2) {
            let priceMinItem = children[0];
            let priceMaxItem = children[1];

            if (this.priceMinISMoreThanPriceMax(priceMinItem, priceMaxItem)) {
                priceMaxItem.clear();
            }

            let value = this.getLabel(priceMinItem.value, priceMaxItem.value);
            this._internals.setFormValue(value);

            this.dispatchEvent(new WlSelected(value, value));
        }
    }

    private priceMinISMoreThanPriceMax(priceMinItem: WlRangeItem, priceMaxItem: WlRangeItem) {
        return priceMinItem.value != ""
            && priceMaxItem.value != "" &&
            priceMaxItem.input != null &&
            parseInt(priceMinItem.value.replace(",", "")) > parseInt(priceMaxItem.value.replace(",", ""));
    }

    private getLabel(priceMin: string, priceMax: string) {
        if (priceMin == "" && priceMax == "") {
            return ""
        } else {
            return priceMin + " - " + priceMax;
        }
    }

    private getChildren() {
        return [...this.childNodes]
            .filter(node => node.nodeType === node.ELEMENT_NODE)
            .filter(node => node.nodeName !== "DIV")
            .map(node => node as WlRangeItem)
    }
}

export class WlRangeItem extends LitElement {
    public value: string;
    public name: string;
    public placeholder: string;
    public form: string;
    public input: HTMLInputElement | null;

    static formAssociated = true;

    // @ts-ignore
    private _internals: ElementInternals & IElementInternals;

    static get properties() {
        return {
            value: {type: String, reflect: true},
            name: {type: String, reflect: true},
            placeholder: {type: String, reflect: true},
            form: {type: String, reflect: true},
        }
    }

    constructor() {
        super();
        this.value = "";
        this.name = "";
        this.placeholder = "";
        this.form = "";
        this._internals = this.attachInternals();
        this.input = null;
    }

    protected firstUpdated(_changedProperties: PropertyValues) {
        this.value = this.value.replace(".0", "");
        this.input = this.renderRoot.querySelector("input")!;
        this.input.value = this.value;
        this.input.name = this.name;
        this.input.placeholder = this.placeholder;
        this.formatInputNumber(this.input);
        this.addInputListeners();
        this.customDispatchEvent();
    }

    static get styles() {
        return css`            
            input {
                width: var(--wl-range-item-width ,${pxToRem(79)});
                height: var(--wl-range-item-height ,${pxToRem(34)});
                padding: 0 ${pxToRem(10)};
                font-size: ${pxToRem(14)};
                color: var(--wl-range-item-color, #000);
                border: 1px solid var(--wl-range-item-border-color, #000);
                border-radius: ${pxToRem(4)};
            }
        `
    }

    protected render() {
        return html`
            <input
                type="text"
                ${this.value ? `value="${this.value}"` : ""}
                ${this.name ? `name="${this.name}"` : ""}
                ${this.placeholder ? `placeholder="${this.placeholder}"` : ""}
                @change="${this.changeValue}"
            >
            <slot></slot>
        `
    }

    clear() {
        this.value = "";
        if (this.input) {
            this.input.value = "";
        }

        this.customDispatchEvent();
    }

    private changeValue() {
        this.addInputListeners();
        this.customDispatchEvent();
    }

    private customDispatchEvent() {
        this._internals.setFormValue(this.value);

        this.dispatchEvent(new CustomEvent("change", {bubbles: true, composed: true}))
    }

    private addInputListeners() {
        if (this.input) {
            this.input.addEventListener('input', (event) => {
                this.formatInputNumber(event.target as HTMLInputElement);
            });
        }
    }

    private formatInputNumber(input: HTMLInputElement) {
        let valueAsNumber = parseInt(input.value.toString().replace(/\D/g, ''), 10);
        if (Number.isNaN(valueAsNumber)) {
            input.value = '';
        } else {
            input.value = valueAsNumber.toLocaleString('th-TH');
        }
        this.value = input.value;
    }
}
